/**
 * DocumentPageViewer — high-resolution, on-demand page viewer for the
 * retained original scan behind a timesheet row.
 *
 * Shared by DocumentPreviewModal (card preview) and ReviewMode's left
 * pane. Loads ONE page at a time from
 * `/timesheets/{id}/document/pages/{n}` (bounded server-side render),
 * upgrading to the `zoom` render when magnification exceeds 150%.
 *
 * Legacy rows without a retained original fall back to the low-res OCR
 * thumbnail with an honest explanation — never a misleading grey icon.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { useAuthedBlobUrl } from '../../hooks/useAuthedBlobUrl';
import {
  ArrowCounterClockwise,
  ArrowClockwise,
  ArrowsHorizontal,
  CaretLeft,
  CaretRight,
  CornersOut,
  Eye,
  EyeSlash,
  FrameCorners,
  MagnifyingGlassMinus,
  MagnifyingGlassPlus,
  Spinner,
  WarningCircle,
} from '@phosphor-icons/react';

const ZOOM_MIN = 50;
const ZOOM_MAX = 400;
const ZOOM_STEP = 25;

const ToolbarButton = ({ onClick, title, disabled, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    data-testid={testId}
    className="p-1.5 rounded text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-30 disabled:cursor-not-allowed"
  >
    {children}
  </button>
);

const LegacyFallback = ({ timesheetId, hasThumbnail, zoomPct }) => {
  const { url, loading } = useAuthedBlobUrl(
    hasThumbnail ? `/timesheets/${timesheetId}/thumbnail` : null,
    hasThumbnail
  );
  return (
    <div className="flex flex-col items-center gap-3 p-4" data-testid="doc-legacy-notice">
      <div className="max-w-md text-center text-xs text-slate-400 bg-slate-800/70 border border-slate-700 rounded-lg px-4 py-3">
        <p className="font-semibold text-slate-200 mb-1">Original scan unavailable</p>
        <p>
          This timesheet was uploaded before original-document retention was
          enabled.{' '}
          {hasThumbnail
            ? 'The available OCR thumbnail is shown below.'
            : 'Source image unavailable for this legacy timesheet.'}
        </p>
      </div>
      {loading && <Spinner size={24} className="animate-spin text-indigo-400" />}
      {url && (
        <img
          src={url}
          alt="Scanned timesheet preview (legacy OCR thumbnail)"
          data-testid="doc-legacy-thumbnail"
          className="shadow-2xl rounded bg-white"
          style={{ width: `${zoomPct}%`, maxWidth: 'none' }}
        />
      )}
    </div>
  );
};

const DocumentPageViewer = ({
  timesheetId,
  onMeta,
  enableKeyboard = false,
  className = '',
  highlights = [],
  activeHighlightId = null,
}) => {
  const [meta, setMeta] = useState(null);
  const [metaError, setMetaError] = useState(false);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(100);
  const [fitMode, setFitMode] = useState('width'); // width | page | custom
  const [rotation, setRotation] = useState(0);
  const [showHighlights, setShowHighlights] = useState(true);
  const [imgBox, setImgBox] = useState(null); // measured img geometry
  const scrollRef = useRef(null);
  const dragRef = useRef(null);
  const imgElRef = useRef(null);

  // Metadata (also emits the audited "viewed" event server-side).
  useEffect(() => {
    let cancelled = false;
    setMeta(null);
    setMetaError(false);
    if (!timesheetId) return undefined;
    api
      .get(`/timesheets/${timesheetId}/document`)
      .then((res) => {
        if (cancelled) return;
        setMeta(res.data);
        setPage(res.data?.available ? res.data.source_page || 1 : 1);
        onMeta?.(res.data);
      })
      .catch(() => {
        if (!cancelled) setMetaError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [timesheetId, onMeta]);

  const available = !!meta?.available;
  const pageCount = meta?.page_count || 1;
  const renderMode = zoom > 150 ? 'zoom' : 'standard';
  const pagePath = available
    ? `/timesheets/${timesheetId}/document/pages/${page}?mode=${renderMode}`
    : null;
  const { url: pageUrl, loading: pageLoading, error: pageError } = useAuthedBlobUrl(
    pagePath,
    available
  );

  const zoomBy = useCallback((delta) => {
    setFitMode('custom');
    setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));
  }, []);
  const goPage = useCallback(
    (delta) => setPage((p) => Math.min(pageCount, Math.max(1, p + delta))),
    [pageCount]
  );
  const resetView = useCallback(() => {
    setZoom(100);
    setFitMode('width');
    setRotation(0);
  }, []);

  // ── OCR field-provenance highlights ─────────────────────────────
  // Boxes arrive normalized to a 0-1000 grid over the full page image.
  // The overlay layer is pinned to the measured on-screen geometry of
  // the <img> so it tracks zoom / fit-mode / container resizes. Only
  // rendered on the timesheet's own source page and at 0° rotation.
  const highlightPage = meta?.available ? meta.source_page || 1 : 1;
  const highlightsVisible =
    showHighlights && rotation === 0 && page === highlightPage &&
    Array.isArray(highlights) && highlights.length > 0;

  const measureImg = useCallback(() => {
    const img = imgElRef.current;
    if (!img || !img.offsetWidth) {
      setImgBox(null);
      return;
    }
    setImgBox({
      left: img.offsetLeft,
      top: img.offsetTop,
      width: img.offsetWidth,
      height: img.offsetHeight,
    });
  }, []);

  useEffect(() => {
    measureImg();
    const img = imgElRef.current;
    if (!img || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measureImg);
    ro.observe(img);
    window.addEventListener('resize', measureImg);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureImg);
    };
  }, [measureImg, page, zoom, fitMode, rotation]);

  // Bring the active field's box into view when the reviewer focuses a
  // form field on the right pane.
  useEffect(() => {
    if (!activeHighlightId || !imgBox || !highlightsVisible) return;
    const h = highlights.find((x) => x.id === activeHighlightId);
    const el = scrollRef.current;
    if (!h || !el || !Array.isArray(h.box) || h.box.length !== 4) return;
    const cy = imgBox.top + ((h.box[1] + h.box[3]) / 2000) * imgBox.height;
    el.scrollTo({ top: Math.max(0, cy - el.clientHeight / 2), behavior: 'smooth' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeHighlightId]);

  // Keyboard: ←/→ pages, Ctrl/Cmd +/- zoom (modal context only).
  useEffect(() => {
    if (!enableKeyboard) return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || tag === 'select') return;
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goPage(-1);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goPage(1);
      } else if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '=')) {
        e.preventDefault();
        zoomBy(ZOOM_STEP);
      } else if ((e.ctrlKey || e.metaKey) && e.key === '-') {
        e.preventDefault();
        zoomBy(-ZOOM_STEP);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enableKeyboard, goPage, zoomBy]);

  // Drag-to-pan when zoomed.
  const onMouseDown = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      left: el.scrollLeft,
      top: el.scrollTop,
    };
  };
  const onMouseMove = (e) => {
    const d = dragRef.current;
    const el = scrollRef.current;
    if (!d || !el) return;
    el.scrollLeft = d.left - (e.clientX - d.x);
    el.scrollTop = d.top - (e.clientY - d.y);
  };
  const endDrag = () => {
    dragRef.current = null;
  };

  let imgStyle = { transform: `rotate(${rotation}deg)` };
  let imgClass = 'shadow-2xl rounded bg-white select-none';
  if (fitMode === 'width') {
    imgClass += ' w-full h-auto';
  } else if (fitMode === 'page') {
    imgClass += ' max-h-full w-auto mx-auto';
  } else {
    imgStyle = { ...imgStyle, width: `${zoom}%`, maxWidth: 'none' };
  }

  const zoomLabel = fitMode === 'custom' ? `${zoom}%` : fitMode === 'width' ? 'Fit W' : 'Fit P';

  return (
    <div className={`flex flex-col h-full min-h-0 ${className}`} data-testid="doc-viewer">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-slate-900 border-b border-slate-700 shrink-0 flex-wrap">
        <div className="flex items-center gap-0.5" role="group" aria-label="Zoom controls">
          <ToolbarButton onClick={() => zoomBy(-ZOOM_STEP)} title="Zoom out" testId="doc-zoom-out">
            <MagnifyingGlassMinus size={16} />
          </ToolbarButton>
          <span className="text-[11px] text-slate-400 w-12 text-center" data-testid="doc-zoom-level">
            {zoomLabel}
          </span>
          <ToolbarButton onClick={() => zoomBy(ZOOM_STEP)} title="Zoom in" testId="doc-zoom-in">
            <MagnifyingGlassPlus size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              setFitMode('width');
              setZoom(100);
            }}
            title="Fit width"
            testId="doc-fit-width"
          >
            <ArrowsHorizontal size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => {
              setFitMode('page');
              setZoom(100);
            }}
            title="Fit page"
            testId="doc-fit-page"
          >
            <FrameCorners size={16} />
          </ToolbarButton>
          <ToolbarButton onClick={resetView} title="Reset view" testId="doc-reset-view">
            <CornersOut size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => setRotation((r) => (r + 270) % 360)}
            title="Rotate left"
            testId="doc-rotate-left"
          >
            <ArrowCounterClockwise size={16} />
          </ToolbarButton>
          <ToolbarButton
            onClick={() => setRotation((r) => (r + 90) % 360)}
            title="Rotate right"
            testId="doc-rotate-right"
          >
            <ArrowClockwise size={16} />
          </ToolbarButton>
          {Array.isArray(highlights) && highlights.length > 0 && (
            <ToolbarButton
              onClick={() => setShowHighlights((v) => !v)}
              title={showHighlights ? 'Hide OCR highlights' : 'Show OCR highlights'}
              testId="doc-highlight-toggle"
            >
              {showHighlights ? <Eye size={16} className="text-amber-400" /> : <EyeSlash size={16} />}
            </ToolbarButton>
          )}
        </div>
        {available && pageCount > 1 && (
          <div className="flex items-center gap-1" role="group" aria-label="Page navigation">
            <ToolbarButton
              onClick={() => goPage(-1)}
              disabled={page <= 1}
              title="Previous page"
              testId="doc-page-prev"
            >
              <CaretLeft size={16} />
            </ToolbarButton>
            <span className="text-[11px] text-slate-400 whitespace-nowrap" data-testid="doc-page-indicator">
              Page {page} of {pageCount}
            </span>
            <ToolbarButton
              onClick={() => goPage(1)}
              disabled={page >= pageCount}
              title="Next page"
              testId="doc-page-next"
            >
              <CaretRight size={16} />
            </ToolbarButton>
          </div>
        )}
      </div>

      {/* Canvas */}
      <div
        ref={scrollRef}
        className="relative flex-1 min-h-0 overflow-auto bg-slate-950 p-4 cursor-grab active:cursor-grabbing"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        data-testid="doc-viewer-canvas"
      >
        {!meta && !metaError && (
          <div className="flex items-center justify-center h-full">
            <Spinner size={28} className="animate-spin text-indigo-400" />
          </div>
        )}
        {metaError && (
          <div className="flex flex-col items-center justify-center h-full text-slate-400 text-sm gap-2" data-testid="doc-error-fallback">
            <WarningCircle size={28} className="text-amber-400" />
            Preview could not be loaded. Try again or download the original.
          </div>
        )}
        {meta && !available && (
          <LegacyFallback
            timesheetId={timesheetId}
            hasThumbnail={!!meta.has_thumbnail}
            zoomPct={fitMode === 'custom' ? zoom : 100}
          />
        )}
        {available && (
          <>
            {(pageLoading || (!pageUrl && !pageError)) && (
              <div className="flex items-center justify-center h-full">
                <Spinner size={28} className="animate-spin text-indigo-400" />
              </div>
            )}
            {pageError && (
              <div className="flex flex-col items-center justify-center h-full text-slate-400 text-sm gap-2" data-testid="doc-page-error">
                <WarningCircle size={28} className="text-amber-400" />
                Preview could not be generated. You can still open or download the original file.
              </div>
            )}
            {pageUrl && !pageLoading && (
              <img
                ref={imgElRef}
                src={pageUrl}
                alt={`Scanned timesheet, page ${page} of ${pageCount}`}
                draggable={false}
                onLoad={measureImg}
                className={imgClass}
                style={imgStyle}
                data-testid="doc-page-image"
              />
            )}
            {highlightsVisible && imgBox && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: imgBox.left,
                  top: imgBox.top,
                  width: imgBox.width,
                  height: imgBox.height,
                }}
                data-testid="ocr-highlight-layer"
              >
                {highlights.map((h) => {
                  if (!Array.isArray(h.box) || h.box.length !== 4) return null;
                  const [x0, y0, x1, y1] = h.box;
                  const active = h.id === activeHighlightId;
                  return (
                    <div
                      key={h.id}
                      className={`absolute rounded-sm transition-colors duration-150 ${
                        active
                          ? 'border-2 border-amber-400 bg-amber-300/30 shadow-[0_0_0_2px_rgba(251,191,36,0.35)]'
                          : 'border border-indigo-400/80 bg-indigo-400/10'
                      }`}
                      style={{
                        left: `${x0 / 10}%`,
                        top: `${y0 / 10}%`,
                        width: `${(x1 - x0) / 10}%`,
                        height: `${(y1 - y0) / 10}%`,
                      }}
                      data-testid={`ocr-highlight-${h.id}`}
                    >
                      {h.label && (
                        <span
                          className={`absolute -top-4 left-0 text-[9px] font-semibold px-1 rounded-sm whitespace-nowrap ${
                            active ? 'bg-amber-400 text-slate-900' : 'bg-indigo-500/90 text-white'
                          }`}
                        >
                          {h.label}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default DocumentPageViewer;
